// Adopt turns the discovered stack of a DYNAMIC I/O provider (e.g. a coupler) into a plan (stage 3, step 3a): every
// slot without a module id gets a new one, written as io.modules.<id> = <article> and io.modules.<id>.slot = <slot>
// into the provider's configuration. Existing ids are never changed or reused, so no I/O reference is ever rewired.

export type DiscoveredModule = {
  slot: number;
  busId: string;
  id?: string;
  article?: string;
  friendlyName?: string;
};

// A module the provider supports (supportedModules of its manifest).
export type SupportedModule = {
  article: string;
  busId: string;
  friendlyName: string;
};

export const modulePrefix = "io.modules.";

// A slot to adopt: the articles the bus id fits, and the planned module it replaces if the slot has a mismatch.
export type AdoptableSlot = {
  slot: number;
  busId: string;
  candidates: Array<SupportedModule>;
  replaces?: string;
};

// plannedModules returns the module ids of the configuration by slot.
export function plannedModules(configuration: Record<string, string>): Map<number, string> {
  const slots = new Map<number, string>();
  for (const [key, value] of Object.entries(configuration)) {
    if (key.startsWith(modulePrefix) && key.endsWith(".slot")) {
      slots.set(parseInt(value), key.slice(modulePrefix.length, -".slot".length));
    }
  }
  return slots;
}

// adoptableSlots returns the slots without a module id, with the articles that fit them.
export function adoptableSlots(modules: Array<DiscoveredModule>, configuration: Record<string, string>, supported: Array<SupportedModule>): Array<AdoptableSlot> {
  const planned = plannedModules(configuration);
  return modules
    .filter((m) => !m.id)
    .map((m) => ({
      slot: m.slot,
      busId: m.busId,
      candidates: supported.filter((s) => s.busId === m.busId),
      replaces: planned.get(m.slot),
    }));
}

// usedIds returns every module id of the configuration, and every one the endpoint description still references
// ("<provider>/<id>/<interface>"), so that a new id never takes over a stale reference.
export function usedIds(providerId: string, configuration: Record<string, string>, otherConfigurations: Array<Record<string, string>>): Set<string> {
  const ids = new Set<string>(plannedModules(configuration).values());
  const reference = new RegExp(providerId.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "/([a-z][a-z0-9-]*)/", "g");
  for (const c of otherConfigurations) {
    for (const value of Object.values(c)) {
      for (const match of String(value).matchAll(reference)) {
        ids.add(match[1]);
      }
    }
  }
  return ids;
}

// newId returns the first free id for a module with the bus id: "do16-1", "m459-2", …
export function newId(busId: string, used: Set<string>): string {
  let kind = busId.toLowerCase().replace(/[^a-z0-9-]/g, "");
  if (!/^[a-z]/.test(kind)) {
    kind = "m" + kind;
  }
  for (let n = 1; ; n++) {
    const id = kind + "-" + n;
    if (!used.has(id)) {
      used.add(id);
      return id;
    }
  }
}

// adopt returns the configuration with a new module for every chosen slot (slot → article). A planned module in a
// chosen slot (a mismatch) is removed with its interface settings.
export function adopt(configuration: Record<string, string>, chosen: Map<number, string>, busIds: Map<number, string>, used: Set<string>): Record<string, string> {
  const planned = plannedModules(configuration);
  const result: Record<string, string> = { ...configuration };
  for (const [slot, article] of Array.from(chosen.entries())) {
    const replaced = planned.get(slot);
    if (replaced) {
      for (const key of Object.keys(result)) {
        if (key === modulePrefix + replaced || key.startsWith(modulePrefix + replaced + ".") || key.startsWith("io.interfaces." + replaced + "/")) {
          delete result[key];
        }
      }
    }
    const id = newId(busIds.get(slot)!, used);
    result[modulePrefix + id] = article;
    result[modulePrefix + id + ".slot"] = String(slot);
  }
  return result;
}
